import { ReactNode } from "react";
import { Theme } from "@carbon/react";
import LogoSpectumWhite from "../../../assets/images/logo-simbuy-white.png";
import "./authLayout.scss";

export interface AuthLayoutProps {
  /** The card's content — the caller's own form/markup, unopinionated. */
  children: ReactNode;
  /**
   * Content column width. "narrow" (the default) fits the small auth forms —
   * login, reset/new password, validate-user, delivery confirmation. "wide"
   * fits a read+form screen with more content, e.g. the anonymous-offer
   * invitation view or the multi-section register form.
   */
  width?: "narrow" | "wide";
  /**
   * When true, render a two-column shell with a full-height Simbuy brand panel
   * on the right (hidden below tablet). Login and Register use it to keep their
   * two-column branding; the other logged-out screens stay single-column.
   */
  brandPanel?: boolean;
}

/**
 * The page shell every bare, logged-out screen renders through — a full-height,
 * DARK (Carbon g100) branded surface: the white Simbuy wordmark above the
 * caller's form, floating directly on the near-black background (no light card).
 * The whole subtree is wrapped in Carbon's `Theme theme="g100"` so every Carbon
 * field/button inside renders on the dark tokens automatically.
 *
 * With `brandPanel`, the form sits in the left column and a full-height dark
 * panel with the centered Simbuy wordmark fills the right (dropped below tablet).
 *
 * The logged-out experience is intentionally dark while the authenticated app is
 * light g10 content under a g100 `AppShell` — the same split IBM's own products
 * use (dark sign-in, light app). `AppShell` is this layout's authenticated
 * counterpart; owned by the primitives layer.
 */
const AuthLayout = ({
  children,
  width = "narrow",
  brandPanel = false,
}: AuthLayoutProps) => {
  const main = (
    <div className="auth-layout__main">
      <div className={`auth-layout__content auth-layout__content--${width}`}>
        <div className="auth-layout__logo">
          <img src={LogoSpectumWhite} alt="Simbuy" />
        </div>
        {children}
      </div>
    </div>
  );

  return (
    <Theme theme="g100">
      <div
        className={`auth-layout${brandPanel ? " auth-layout--split" : ""}`}
      >
        {main}
        {brandPanel ? (
          <aside className="auth-layout__brand" aria-hidden="true">
            <img src={LogoSpectumWhite} alt="" />
          </aside>
        ) : null}
      </div>
    </Theme>
  );
};

export default AuthLayout;
